import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Shield,
  Users,
  Briefcase,
  AlertTriangle,
  CheckCircle2,
  X,
  Search,
  Flag,
  Eye,
  Settings,
  LogOut,
  TrendingUp,
  DollarSign,
  UserCheck,
  Calendar,
  Edit,
  Ban,
  Image as ImageIcon,
  FileText,
  Upload,
  Save,
  Trash2,
  Building2,
  ShieldX
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Switch } from '../ui/switch';
import { RoleBadge } from '../common/RoleBadge';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { fetchAdminAnalytics } from '../../api/adminAnalytics';
import { fetchAdminModerationJobs } from '../../api/adminModeration';
import { fetchAdminCandidates } from '../../api/adminCandidates';
import { fetchAdminEmployers } from '../../api/adminEmployers';
import { useAuth } from '../../hooks/useAuth';
import { Helmet } from 'react-helmet-async';

type AdminRole = 'sysadmin' | 'moderator' | 'owner';

const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  sysadmin: 'System Admin',
  moderator: 'Moderator',
  owner: 'Content Owner'
};

export function AdminPortal() {
  const [adminRole, setAdminRole] = useState<AdminRole>('sysadmin'); // In real app, this would come from auth
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };
  const handleNavigateUploads = () => navigate('/admin/uploads');
  const analyticsQuery = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: fetchAdminAnalytics,
    staleTime: 1000 * 60,
  });

  const moderationQuery = useQuery({
    queryKey: ['admin-moderation-overview'],
    queryFn: () => fetchAdminModerationJobs(),
    staleTime: 1000 * 60,
  });

  const candidatesQuery = useQuery({
    queryKey: ['admin-candidates-overview'],
    queryFn: () => fetchAdminCandidates({}),
    staleTime: 1000 * 60,
  });

  const employersQuery = useQuery({
    queryKey: ['admin-employers-overview'],
    queryFn: () => fetchAdminEmployers({}),
    staleTime: 1000 * 60,
  });

  const stats =
    analyticsQuery.data?.stats?.map((stat, index) => ({
      label: stat.label,
      value: stat.value.toLocaleString(),
      icon: [Users, Briefcase, Building2, DollarSign][index] ?? Users,
      change: `${(stat.delta * 100).toFixed(1)}%`,
      changeType: stat.delta >= 0 ? 'positive' : 'negative',
    })) ?? [];

  type RecentUserCard = {
    id: string;
    name: string;
    email: string;
    type: 'Candidate' | 'Employer';
    joined: string;
    status: string;
    applications?: number;
    jobPosts?: number;
  };

  type RecentJobCard = {
    id: string | number;
    title: string;
    company: string;
    status: string;
    reports: number;
    applicants?: number;
    views?: number;
    posted?: string;
  };

  type CompanyCard = {
    id: string;
    name: string;
    status: string;
    subscription: string;
    industry: string;
    location: string;
    employees: string;
    joined: string;
    activeJobs: number;
    totalJobs: number;
  };

  const candidates = candidatesQuery.data ?? [];
  const employers = employersQuery.data ?? [];
  const moderationJobs = moderationQuery.data ?? [];

  const recentUsers: RecentUserCard[] = [
    ...candidates.slice(0, 5).map((candidate) => ({
      id: candidate.id,
      name: candidate.name,
      email: candidate.email,
      type: 'Candidate' as const,
      joined: candidate.joinedAt
        ? new Date(candidate.joinedAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : '',
      status: candidate.status,
      applications: undefined,
    })),
    ...employers.slice(0, 5).map((employer) => ({
      id: employer.id,
      name: employer.companyName,
      email: employer.contactEmail,
      type: 'Employer' as const,
      joined: employer.submittedAt
        ? new Date(employer.submittedAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : '',
      status: employer.status,
      jobPosts: undefined,
    })),
  ].slice(0, 10);

  const recentJobs: RecentJobCard[] = moderationJobs.slice(0, 10).map((job) => ({
    id: job.id,
    title: job.title,
    company: job.employerName,
    status: job.status,
    reports: job.reports,
    applicants: undefined,
    views: undefined,
    posted: job.submittedAt
      ? new Date(job.submittedAt).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '',
  }));

  const companyCards: CompanyCard[] = employers.slice(0, 12).map((employer) => ({
    id: employer.id,
    name: employer.companyName,
    status: employer.status,
    subscription: 'Free',
    industry: 'Healthcare',
    location: '—',
    employees: '—',
    joined: employer.submittedAt
      ? new Date(employer.submittedAt).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '',
    activeJobs: 0,
    totalJobs: 0,
  }));

  const getStatValue = (label: string) => analyticsQuery.data?.stats.find((stat) => stat.label === label)?.value ?? 0;
  const getStatDelta = (label: string) => analyticsQuery.data?.stats.find((stat) => stat.label === label)?.delta ?? 0;
  const derivePrevious = (current: number, delta: number) => {
    if (!delta) return current;
    const denominator = 1 + delta;
    if (!Number.isFinite(denominator) || Math.abs(denominator) < 0.0001) return current;
    const previous = current / denominator;
    return Number.isFinite(previous) ? Math.max(0, Math.round(previous)) : current;
  };

  const activeCandidatesValue = getStatValue('Active candidates');
  const verifiedEmployersValue = getStatValue('Verified employers');
  const returnerPlacementsValue = getStatValue('Returner placements');
  const candidatesDelta = getStatDelta('Active candidates');
  const employersDelta = getStatDelta('Verified employers');
  const returnersDelta = getStatDelta('Returner placements');

  const userGrowthData = [
    {
      month: 'Previous',
      candidates: derivePrevious(activeCandidatesValue, candidatesDelta),
      employers: derivePrevious(verifiedEmployersValue, employersDelta),
      total:
        derivePrevious(activeCandidatesValue, candidatesDelta) +
        derivePrevious(verifiedEmployersValue, employersDelta),
    },
    {
      month: 'Current',
      candidates: activeCandidatesValue,
      employers: verifiedEmployersValue,
      total: activeCandidatesValue + verifiedEmployersValue,
    },
  ];

  const pendingModeration = moderationJobs.filter((job) => job.status === 'pending_review').length;
  const flaggedModeration = moderationJobs.filter((job) => job.status === 'flagged').length;
  const resolvedModeration = moderationJobs.filter((job) => job.status === 'approved' || job.status === 'rejected').length;

  const jobPostingTrends = [
    {
      month: 'Pending',
      posts: pendingModeration,
      applications: Math.max(0, analyticsQuery.data?.backlog.supportTickets ?? 0),
    },
    {
      month: 'Flagged',
      posts: flaggedModeration,
      applications: Math.max(0, analyticsQuery.data?.backlog.moderation ?? 0),
    },
    {
      month: 'Resolved',
      posts: resolvedModeration,
      applications: Math.max(0, returnerPlacementsValue),
    },
  ];

  const revenueData = [
    {
      month: 'Previous',
      subscriptions: derivePrevious(verifiedEmployersValue, employersDelta),
      boosts: derivePrevious(returnerPlacementsValue, returnersDelta),
      total:
        derivePrevious(verifiedEmployersValue, employersDelta) +
        derivePrevious(returnerPlacementsValue, returnersDelta),
    },
    {
      month: 'Current',
      subscriptions: verifiedEmployersValue,
      boosts: returnerPlacementsValue,
      total: verifiedEmployersValue + returnerPlacementsValue,
    },
  ];

  const statusCounts = employers.reduce<Record<string, number>>((acc, employer) => {
    acc[employer.status] = (acc[employer.status] ?? 0) + 1;
    return acc;
  }, {});
  const totalEmployers = employers.length || 1;
  const subscriptionDistribution = [
    { name: 'Approved', users: statusCounts.approved ?? 0 },
    { name: 'Pending', users: statusCounts.pending ?? 0 },
    { name: 'Needs Info', users: statusCounts.needs_more_info ?? 0 },
    { name: 'Rejected', users: statusCounts.rejected ?? 0 },
  ]
    .filter((entry) => entry.users > 0)
    .map((entry) => ({
      ...entry,
      value: Number(((entry.users / totalEmployers) * 100).toFixed(1)),
    }));

  const COLORS = ['#EAB308', '#22C55E', '#3B82F6', '#A855F7'];

  const recentReports = moderationJobs
    .filter((job) => job.status === 'flagged' || job.status === 'pending_review' || job.reports > 0)
    .slice(0, 10)
    .map((job, index) => ({
      id: job.id,
      type: 'Job Post',
      reporter: job.employerName,
      reportedItem: job.title,
      reason: job.description || 'Submitted for moderation review.',
      status:
        job.status === 'approved' || job.status === 'rejected'
          ? 'resolved'
          : job.status === 'flagged'
          ? 'reviewing'
          : 'pending',
      date: new Date(job.submittedAt).toISOString().slice(0, 10),
      priority: job.reports >= 3 ? 'high' : job.reports >= 1 ? 'medium' : 'low',
      reportedId: String(job.id),
      reports: job.reports,
      sourceStatus: job.status,
      sourceType: 'job',
    }));

  const platformActivity =
    analyticsQuery.data?.charts.weeklyActivity.map((entry) => ({
      time: entry.label,
      users: entry.value,
      jobs: Math.max(0, moderationJobs.length),
    })) ?? [];

  const [banners, setBanners] = useState([
    { id: 1, title: 'Christmas Special', description: 'Holiday season offers', active: true, startDate: '2025-12-01', endDate: '2025-12-31' },
    { id: 2, title: 'Summer Jobs Fair', description: 'Connect with top employers', active: false, startDate: '2025-06-01', endDate: '2025-06-30' },
  ]);

  const hasAccess = (requiredRoles: AdminRole[]) => {
    return requiredRoles.includes(adminRole);
  };

  const handleSuspendItem = (type: string, itemId: string) => {
    toast.success(`${type} has been suspended`);
  };

  const handleResolveReport = (reportId: string | number) => {
    toast.success('Report has been resolved');
  };

  const handleEditUser = (user: any) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleSaveUser = () => {
    toast.success('User information updated successfully');
    setEditDialogOpen(false);
  };

  const handleToggleBanner = (bannerId: number) => {
    setBanners(banners.map(b => b.id === bannerId ? { ...b, active: !b.active } : b));
    toast.success('Banner status updated');
  };

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]"
      aria-labelledby="admin-dashboard-title"
    >
      <Helmet>
        <title>Admin Dashboard | Lighthouse Jobs</title>
        <meta
          name="description"
          content="Administer analytics, moderation, users, companies, and platform settings across Lighthouse."
        />
      </Helmet>
      {/* Header */}
      <header className="border-b border-white/10 bg-[#1a1d2e]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gold-gradient flex items-center justify-center">
                  <Shield className="w-5 h-5 text-black" />
                </div>
                <span className="text-gold-gradient">
                  Admin Portal
                </span>
              </Link>
              
              <RoleBadge role="admin" label={ADMIN_ROLE_LABELS[adminRole]} />

              {/* Role Switcher (for demo purposes) */}
              <Select value={adminRole} onValueChange={(value) => setAdminRole(value as AdminRole)}>
                <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                  <SelectItem value="sysadmin">Switch to Sysadmin</SelectItem>
                  <SelectItem value="moderator">Switch to Moderator</SelectItem>
                  <SelectItem value="owner">Switch to Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                type="button"
                className="bg-gold-gradient text-black hover:shadow-[0_0_20px_rgba(234,179,8,0.5)]"
                onClick={() => navigate('/admin/uploads')}
              >
                <ShieldX className="mr-2 h-4 w-4" />
                AV DLQ
              </Button>
              <Link to="/admin/settings">
                <Button variant="ghost" size="icon" className="text-white">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-white"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 space-y-2">
          <RoleBadge role="admin" label={ADMIN_ROLE_LABELS[adminRole]} />
          <h1 id="admin-dashboard-title" className="mb-2 text-gold-gradient">
            Admin Dashboard
          </h1>
          <p className="text-gray-400">
            {adminRole === 'sysadmin' && 'Full platform management and system administration'}
            {adminRole === 'moderator' && 'Content moderation and report management'}
            {adminRole === 'owner' && 'Content management, moderation, and platform updates'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            // Show revenue only to sysadmin
            if (stat.label.includes('Revenue') && !hasAccess(['sysadmin'])) return null;
            return (
              <Card
                key={index}
                className="role-surface-admin bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center">
                      <Icon className="w-6 h-6 text-black" />
                    </div>
                  </div>
                  <div className="text-white mb-1">{stat.value}</div>
                  <p className="text-sm text-gray-400 mb-2">{stat.label}</p>
                  <p className={`text-xs ${stat.changeType === 'positive' ? 'text-green-400' : 'text-red-400'}`}>
                    {stat.change} from last month
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue={adminRole === 'moderator' ? 'reports' : 'analytics'} aria-label="Admin dashboard sections">
          <nav aria-label="Admin dashboard navigation">
            <TabsList className="bg-white/5 border border-white/10 mb-8">
              {hasAccess(['sysadmin']) && (
                <TabsTrigger value="analytics" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black text-white">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Analytics
                </TabsTrigger>
              )}
              {hasAccess(['sysadmin', 'moderator', 'owner']) && (
                <TabsTrigger value="reports" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black text-white">
                  <Flag className="w-4 h-4 mr-2" />
                  Reports
                </TabsTrigger>
              )}
              {hasAccess(['sysadmin', 'owner']) && (
                <TabsTrigger value="users" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black text-white">
                  <Users className="w-4 h-4 mr-2" />
                  Users
                </TabsTrigger>
              )}
              {hasAccess(['sysadmin', 'owner']) && (
                <TabsTrigger value="jobs" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black text-white">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Jobs
                </TabsTrigger>
              )}
              {hasAccess(['sysadmin', 'owner']) && (
                <TabsTrigger value="companies" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black text-white">
                  <Building2 className="w-4 h-4 mr-2" />
                  Companies
                </TabsTrigger>
              )}
              {hasAccess(['owner', 'sysadmin']) && (
                <TabsTrigger value="content" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black text-white">
                  <FileText className="w-4 h-4 mr-2" />
                  Content
                </TabsTrigger>
              )}
              {hasAccess(['owner']) && (
                <TabsTrigger value="banners" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black text-white">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Banners
                </TabsTrigger>
              )}
              {hasAccess(['sysadmin']) && (
                <TabsTrigger value="uploads" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black text-white">
                  <ShieldX className="w-4 h-4 mr-2" />
                  AV DLQ
                </TabsTrigger>
              )}
            </TabsList>
          </nav>

          {/* Analytics Tab - Sysadmin Only */}
          {hasAccess(['sysadmin']) && (
            <TabsContent value="analytics" className="space-y-6">
              {/* User Growth Chart */}
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">User Growth Trends</CardTitle>
                  <CardDescription className="text-gray-400">
                    Platform user growth over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={userGrowthData}>
                      <defs>
                        <linearGradient id="colorCandidates" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#EAB308" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#EAB308" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorEmployers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22C55E" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="month" stroke="#888" />
                      <YAxis stroke="#888" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        itemStyle={{ color: '#fff' }}
                      />
                      <Legend wrapperStyle={{ color: '#fff' }} />
                      <Area type="monotone" dataKey="candidates" stroke="#EAB308" fillOpacity={1} fill="url(#colorCandidates)" />
                      <Area type="monotone" dataKey="employers" stroke="#22C55E" fillOpacity={1} fill="url(#colorEmployers)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Job Posting Trends */}
                <Card className="role-surface-admin bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Job Posting Activity</CardTitle>
                    <CardDescription className="text-gray-400">
                      Posts and applications over time
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={jobPostingTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="month" stroke="#888" />
                        <YAxis stroke="#888" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff' }}
                        />
                        <Legend wrapperStyle={{ color: '#fff' }} />
                        <Line type="monotone" dataKey="posts" stroke="#EAB308" strokeWidth={2} />
                        <Line type="monotone" dataKey="applications" stroke="#3B82F6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Subscription Distribution */}
                <Card className="role-surface-admin bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Subscription Distribution</CardTitle>
                    <CardDescription className="text-gray-400">
                      User distribution by plan type
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={subscriptionDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {subscriptionDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-4 space-y-2">
                      {subscriptionDistribution.map((plan, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }} />
                            <span className="text-gray-300">{plan.name}</span>
                          </div>
                          <span className="text-white">{plan.users.toLocaleString()} users</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue Analytics */}
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Revenue Overview</CardTitle>
                  <CardDescription className="text-gray-400">
                    Monthly revenue breakdown by source
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="month" stroke="#888" />
                      <YAxis stroke="#888" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        itemStyle={{ color: '#fff' }}
                        formatter={(value) => `£${value.toLocaleString()}`}
                      />
                      <Legend wrapperStyle={{ color: '#fff' }} />
                      <Bar dataKey="subscriptions" fill="#EAB308" name="Subscriptions" />
                      <Bar dataKey="boosts" fill="#22C55E" name="Job Boosts" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Platform Activity */}
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Today's Activity</CardTitle>
                  <CardDescription className="text-gray-400">
                    Real-time platform usage
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={platformActivity}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="time" stroke="#888" />
                      <YAxis stroke="#888" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff' }}
                      />
                      <Legend wrapperStyle={{ color: '#fff' }} />
                      <Area type="monotone" dataKey="users" stroke="#EAB308" fill="#EAB308" fillOpacity={0.3} name="Active Users" />
                      <Area type="monotone" dataKey="jobs" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} name="Jobs Viewed" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Reports Tab - Moderator, Owner, Sysadmin */}
          {hasAccess(['sysadmin', 'moderator', 'owner']) && (
            <TabsContent value="reports">
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white">Content Reports</CardTitle>
                      <CardDescription className="text-gray-400">
                        Review and moderate reported content
                      </CardDescription>
                    </div>
                    <Select defaultValue="all">
                      <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                        <SelectItem value="all">All Reports</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="reviewing">Under Review</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {recentReports.map((report) => (
                    <Card
                      key={report.id}
                      className="role-surface-admin bg-white/5 border-white/10 hover:border-[#d2ac47]/50 transition-all"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="border-white/20 text-gray-300">
                                {report.type}
                              </Badge>
                              <Badge
                                className={
                                  report.status === 'pending'
                                    ? 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
                                    : report.status === 'reviewing'
                                    ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                                    : 'bg-green-500/20 text-green-400 border-green-500/50'
                                }
                              >
                                {report.status}
                              </Badge>
                              <Badge
                                className={
                                  report.priority === 'high'
                                    ? 'bg-red-500/20 text-red-400 border-red-500/50'
                                    : report.priority === 'medium'
                                    ? 'bg-orange-500/20 text-orange-400 border-orange-500/50'
                                    : 'bg-gray-500/20 text-gray-400 border-gray-500/50'
                                }
                              >
                                {report.priority}
                              </Badge>
                            </div>
                            <p className="text-white mb-1">{report.reportedItem}</p>
                            <p className="text-sm text-gray-300 mb-1">Reason: {report.reason}</p>
                            <p className="text-sm text-gray-400">
                              Reported by {report.reporter} on {report.date}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Review
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                className="bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30"
                              >
                                <Ban className="w-4 h-4 mr-2" />
                                Suspend
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">Suspend {report.type}?</AlertDialogTitle>
                                <AlertDialogDescription className="text-gray-400">
                                  This will temporarily suspend the {report.type.toLowerCase()} and notify the user. This action can be reversed later.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleSuspendItem(report.type, report.reportedId)}
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                >
                                  Suspend
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <Button
                            size="sm"
                            onClick={() => handleResolveReport(report.id)}
                            className="bg-green-500/20 text-green-400 border border-green-500/50 hover:bg-green-500/30"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Resolve
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Users Tab - Owner & Sysadmin */}
          {hasAccess(['sysadmin', 'owner']) && (
            <TabsContent value="users">
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white">User Management</CardTitle>
                      <CardDescription className="text-gray-400">
                        Manage candidate and employer accounts
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search users..."
                          className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {recentUsers.map((user) => (
                    <Card
                      key={user.id}
                      className="role-surface-admin bg-white/5 border-white/10 hover:border-[#d2ac47]/50 transition-all"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <Avatar>
                              <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black">
                                {user.name.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="text-white mb-1">{user.name}</p>
                              <p className="text-sm text-gray-400 mb-2">{user.email}</p>
                              <div className="flex items-center gap-4 text-sm">
                                <Badge variant="outline" className="border-white/20 text-gray-300">
                                  {user.type}
                                </Badge>
                                <span className="text-gray-400">Joined {user.joined}</span>
                                <span className="text-gray-400">
                                  {user.type === 'Candidate'
                                    ? `${user.applications ?? 0} applications`
                                    : `${user.jobPosts ?? 0} job posts`}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge
                              className={
                                user.status === 'active'
                                  ? 'bg-green-500/20 text-green-400 border-green-500/50'
                                  : 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
                              }
                            >
                              {user.status}
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditUser(user)}
                              className="border-white/20 text-white hover:bg-white/5"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Jobs Tab - Owner & Sysadmin */}
          {hasAccess(['sysadmin', 'owner']) && (
            <TabsContent value="jobs">
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white">Job Management</CardTitle>
                      <CardDescription className="text-gray-400">
                        Monitor and manage job postings
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search jobs..."
                          className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {recentJobs.map((job) => (
                    <Card
                      key={job.id}
                      className="role-surface-admin bg-white/5 border-white/10 hover:border-[#d2ac47]/50 transition-all"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="text-white">{job.title}</p>
                              <Badge
                                className={
                                  job.status === 'approved'
                                    ? 'bg-green-500/20 text-green-400 border-green-500/50'
                                    : job.status === 'flagged' || job.status === 'rejected'
                                    ? 'bg-red-500/20 text-red-400 border-red-500/50'
                                    : 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
                                }
                              >
                                {job.status.replace(/_/g, ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-400 mb-2">{job.company}</p>
                            <div className="flex items-center gap-6 text-sm text-gray-400">
                              {job.applicants != null && <span>{job.applicants} applicants</span>}
                              {job.views != null && <span>{job.views} views</span>}
                              {typeof job.reports === 'number' && job.reports > 0 && (
                                <span className="text-red-400">{job.reports} reports</span>
                              )}
                              {job.posted && <span>Posted {job.posted}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Companies Tab - Sysadmin & Owner */}
          {hasAccess(['sysadmin', 'owner']) && (
            <TabsContent value="companies" className="space-y-6">
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white">Company Management</CardTitle>
                      <CardDescription className="text-gray-400">
                        Manage registered employers and companies
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Select defaultValue="all">
                        <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                          <SelectItem value="all">All Companies</SelectItem>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="pending">Pending Verification</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search companies..."
                          className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {companyCards.map((company) => (
                    <Card
                      key={company.id}
                      className="role-surface-admin bg-white/5 border-white/10 hover:border-[#d2ac47]/50 transition-all"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start gap-4 flex-1">
                            <Avatar className="w-12 h-12">
                              <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black">
                                {company.name.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-white">{company.name}</h3>
                                <Badge
                                  className={
                                    company.status === 'verified'
                                      ? 'bg-green-500/20 text-green-400 border-green-500/50'
                                      : company.status === 'pending'
                                      ? 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
                                      : 'bg-red-500/20 text-red-400 border-red-500/50'
                                  }
                                >
                                  {company.status === 'verified' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                  {company.status === 'pending' && <AlertTriangle className="w-3 h-3 mr-1" />}
                                  {company.status === 'suspended' && <X className="w-3 h-3 mr-1" />}
                                  {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="border-white/20 text-gray-300"
                                >
                                  {company.subscription}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-400 mb-3">
                                <div>
                                  <p className="text-xs text-gray-500">Industry</p>
                                  <p className="text-white">{company.industry}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Location</p>
                                  <p className="text-white">{company.location}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Employees</p>
                                  <p className="text-white">{company.employees}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Joined</p>
                                  <p className="text-white">{company.joined}</p>
                                </div>
                              </div>
                              <div className="flex gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                  <Briefcase className="w-4 h-4 text-[#ba9036]" />
                                  <span className="text-white">{company.activeJobs} active</span>
                                  <span className="text-gray-400">/ {company.totalJobs} total jobs</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View Details
                          </Button>
                          {company.status === 'pending' && (
                            <Button
                              size="sm"
                              className="bg-gold-gradient text-black hover:opacity-90"
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Verify Company
                            </Button>
                          )}
                          {company.status === 'verified' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Suspend
                            </Button>
                          )}
                          {company.status === 'suspended' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Reactivate
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            <Briefcase className="w-3 h-3 mr-1" />
                            View Jobs
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Content Tab - Owner Only */}
          {hasAccess(['owner', 'sysadmin']) && (
            <TabsContent value="content" className="space-y-6">
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Website Content Admin</CardTitle>
                  <CardDescription className="text-gray-400">
                    Manage marketing-site jobs and blog posts from dedicated admin screens.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  <Button asChild className="bg-gold-gradient text-black hover:brightness-110">
                    <Link to="/admin/www/jobs">Manage WWW Jobs</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Link to="/admin/www/blog">Manage WWW Blog</Link>
                  </Button>
                </CardContent>
              </Card>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Post Job */}
                <Card className="role-surface-admin bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Post Job</CardTitle>
                    <CardDescription className="text-gray-400">
                      Create and publish job postings
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-white">Job Title</Label>
                      <Input className="bg-white/10 border-white/20 text-white mt-1" placeholder="e.g., Senior Care Assistant" />
                    </div>
                    <div>
                      <Label className="text-white">Company</Label>
                      <Input className="bg-white/10 border-white/20 text-white mt-1" placeholder="Company name" />
                    </div>
                    <div>
                      <Label className="text-white">Description</Label>
                      <Textarea className="bg-white/10 border-white/20 text-white mt-1" rows={4} placeholder="Job description..." />
                    </div>
                    <Button className="w-full bg-gold-gradient text-black hover:shadow-[0_0_20px_rgba(234,179,8,0.5)]">
                      <Upload className="w-4 h-4 mr-2" />
                      Publish Job
                    </Button>
                  </CardContent>
                </Card>

                {/* Post Blog */}
                <Card className="role-surface-admin bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Post Blog</CardTitle>
                    <CardDescription className="text-gray-400">
                      Create and publish blog articles
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-white">Blog Title</Label>
                      <Input className="bg-white/10 border-white/20 text-white mt-1" placeholder="Article title" />
                    </div>
                    <div>
                      <Label className="text-white">Category</Label>
                      <Select>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                          <SelectItem value="tips">Career Tips</SelectItem>
                          <SelectItem value="industry">Industry News</SelectItem>
                          <SelectItem value="guides">Guides</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white">Content</Label>
                      <Textarea className="bg-white/10 border-white/20 text-white mt-1" rows={4} placeholder="Blog content..." />
                    </div>
                    <Button className="w-full bg-gold-gradient text-black hover:shadow-[0_0_20px_rgba(234,179,8,0.5)]">
                      <Upload className="w-4 h-4 mr-2" />
                      Publish Blog
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Banners Tab - Owner Only */}
          {hasAccess(['owner']) && (
            <TabsContent value="banners">
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Seasonal Banners</CardTitle>
                  <CardDescription className="text-gray-400">
                    Manage promotional banners and seasonal campaigns
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Add New Banner */}
                  <Card className="role-surface-admin bg-white/5 border-white/10">
                    <CardContent className="p-6 space-y-4">
                      <h3 className="text-white mb-4">Create New Banner</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white">Banner Title</Label>
                          <Input className="bg-white/10 border-white/20 text-white mt-1" placeholder="e.g., Christmas Special" />
                        </div>
                        <div>
                          <Label className="text-white">Upload Image</Label>
                          <div className="mt-1 border-2 border-dashed border-white/20 rounded-lg p-4 text-center hover:border-[#d2ac47]/50 transition-all cursor-pointer">
                            <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">Click to upload</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white">Description</Label>
                        <Input className="bg-white/10 border-white/20 text-white mt-1" placeholder="Banner description" />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white">Start Date</Label>
                          <Input type="date" className="bg-white/10 border-white/20 text-white mt-1" />
                        </div>
                        <div>
                          <Label className="text-white">End Date</Label>
                          <Input type="date" className="bg-white/10 border-white/20 text-white mt-1" />
                        </div>
                      </div>
                      <Button className="w-full bg-gold-gradient text-black hover:shadow-[0_0_20px_rgba(234,179,8,0.5)]">
                        <Save className="w-4 h-4 mr-2" />
                        Create Banner
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Existing Banners */}
                  <div className="space-y-4">
                    <h3 className="text-white">Existing Banners</h3>
                    {banners.map((banner) => (
                      <Card key={banner.id} className="role-surface-admin bg-white/5 border-white/10 hover:border-[#d2ac47]/50 transition-all">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <p className="text-white">{banner.title}</p>
                                <Badge className={banner.active ? 'bg-green-500/20 text-green-400 border-green-500/50' : 'bg-gray-500/20 text-gray-400 border-gray-500/50'}>
                                  {banner.active ? 'Active' : 'Inactive'}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-400 mb-2">{banner.description}</p>
                              <p className="text-xs text-gray-500">
                                {banner.startDate} - {banner.endDate}
                              </p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <Label className="text-white text-sm">Active</Label>
                                <Switch checked={banner.active} onCheckedChange={() => handleToggleBanner(banner.id)} />
                              </div>
                              <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/5">
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </Button>
                              <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* AV DLQ + Maintenance - Sysadmin */}
          {hasAccess(['sysadmin']) && (
            <TabsContent value="uploads" className="space-y-6">
              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShieldX className="h-5 w-5 text-amber-300" />
                    Antivirus DLQ
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Review failed scans, retry or force retry, and purge quarantined files.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-3 text-sm text-gray-300">
                  <Button
                    className="bg-gold-gradient text-black hover:shadow-[0_0_20px_rgba(234,179,8,0.4)]"
                    onClick={() => navigate('/admin/uploads')}
                  >
                    Open DLQ
                  </Button>
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" onClick={() => navigate('/admin/uploads')}>
                    Manage uploads
                  </Button>
                  <div className="text-xs text-gray-500">Requires admin role</div>
                </CardContent>
              </Card>

              <Card className="role-surface-admin bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trash2 className="h-5 w-5 text-amber-300" />
                    Branding Storage Cleanup
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Remove orphaned employer branding assets in R2 and report deleted files/bytes. Run from a secure admin terminal.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-gray-300">
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 font-mono text-xs text-gray-200">
                    <pre className="whitespace-pre-wrap">{`cd apps/api
NEON_DATABASE_URL="postgres://..." \\
R2_ACCOUNT_ID="..." \\
R2_ACCESS_KEY_ID="..." \\
R2_SECRET_ACCESS_KEY="..." \\
R2_BUCKET_PRIVATE="cv-uploads" \\
R2_BUCKET_PUBLIC="cdn-assets" \\
R2_PUBLIC_BASE_URL="https://pub-xxxx.r2.dev" \\
DRY_RUN=true \\
node scripts/cleanupBrandingUploads.mjs`}</pre>
                  </div>
                  <div className="text-xs text-gray-500">
                    Output includes scanned uploads, orphaned count, removed objects, and total data removed. Set DRY_RUN=false to delete.
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Edit User Information</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update user profile and account settings
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Name</Label>
                  <Input defaultValue={selectedUser.name} className="bg-white/10 border-white/20 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-white">Email</Label>
                  <Input defaultValue={selectedUser.email} className="bg-white/10 border-white/20 text-white mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Type</Label>
                  <Select defaultValue={selectedUser.type}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                      <SelectItem value="Candidate">Candidate</SelectItem>
                      <SelectItem value="Employer">Employer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Status</Label>
                  <Select defaultValue={selectedUser.status}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-white/20 text-white hover:bg-white/5">
              Cancel
            </Button>
            <Button onClick={handleSaveUser} className="bg-gold-gradient text-black hover:shadow-[0_0_20px_rgba(234,179,8,0.5)]">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
